import React, { useRef, useEffect } from 'react';
import { safeClipboardRead } from '@soVendor/utils/clipboard';

/** Escape HTML and convert markdown to HTML so editor shows paragraphs, lists, and formatting */
function markdownToHtml(md: string): string {
  if (md == null || typeof md !== 'string') return '';
  const esc = md
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const withFormat = esc
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<span class="line-through">$1</span>')
    .replace(/__(.+?)__/g, '<span class="underline">$1</span>');
  const paragraphs = withFormat.split(/\n\n+/);
  const blocks = paragraphs.map((p) => {
    const withBreaks = p.replace(/\n/g, '<br>');
    return withBreaks ? `<div>${withBreaks}</div>` : '<div><br></div>';
  });
  return blocks.length ? blocks.join('') : '<div><br></div>';
}

/** Convert contenteditable HTML back to markdown: preserves paragraphs, lists, and bold/italic/strikethrough/underline */
function htmlToMarkdown(html: string): string {
  if (!html || typeof html !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = html.replace(/<br\s*\/?>/gi, '\n');
  function visit(n: Node, parentTag?: string): string {
    if (n.nodeType === Node.TEXT_NODE) return n.textContent || '';
    if (n.nodeType !== Node.ELEMENT_NODE) return '';
    const el = n as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const inner = Array.from(n.childNodes).map((c) => visit(c, tag)).join('');
    if (tag === 'strong' || tag === 'b') return '**' + inner + '**';
    if (tag === 'em' || tag === 'i') return '*' + inner + '*';
    if (tag === 'u') return '__' + inner + '__';
    if (tag === 'span') {
      if (el.classList.contains('line-through')) return '~~' + inner + '~~';
      if (el.classList.contains('underline')) return '__' + inner + '__';
      const style = (el.getAttribute('style') || '').toLowerCase();
      if (style.includes('font-weight') && /bold|700|8|9\d\d/.test(style)) return '**' + inner + '**';
      if (style.includes('font-style') && style.includes('italic')) return '*' + inner + '*';
      if (style.includes('text-decoration') && style.includes('line-through')) return '~~' + inner + '~~';
      if (style.includes('text-decoration') && style.includes('underline')) return '__' + inner + '__';
    }
    if (tag === 'li') return (parentTag === 'ul' ? '\n• ' : '\n1. ') + inner.trim().replace(/\n+/g, ' ');
    if (tag === 'ul' || tag === 'ol') return Array.from(n.childNodes).map((c) => visit(c, tag)).join('') + '\n';
    if (tag === 'div' || tag === 'p') return inner + '\n\n';
    return inner;
  }
  const out = Array.from(div.childNodes).map((n) => visit(n)).join('');
  return out.replace(/\n{3,}/g, '\n\n').trim();
}

export const MARKDOWN_SECTION_EDITOR_ID = 'markdown-section-editor';

interface MarkdownSectionEditorProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  minHeight?: string;
}

/**
 * WYSIWYG section editor: shows bold/italic etc. without ** or * in the editor.
 * Stores content as markdown; syncs to contenteditable for display and back on input.
 */
const MarkdownSectionEditor: React.FC<MarkdownSectionEditorProps> = ({
  id,
  value,
  onChange,
  className = '',
  placeholder = '',
  minHeight = '200px',
}) => {
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el || !el.isContentEditable) return;
    const markdown = typeof value === 'string' ? value : '';
    const html = markdownToHtml(markdown);
    const target = html || '';
    if (el.innerHTML !== target) {
      el.innerHTML = target;
    }
  }, [value]);

  const handleInput = () => {
    const el = elRef.current;
    if (!el) return;
    const markdown = htmlToMarkdown(el.innerHTML);
    onChange(markdown);
  };

  return (
    <div
      ref={elRef}
      id={id}
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      aria-label={placeholder || 'Section content'}
      data-placeholder={placeholder}
      className={`w-full px-3 py-2.5 text-sm border-0 focus:ring-0 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-y min-h-[200px] [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-gray-400 dark:[&:empty]:before:text-gray-500 ${className}`}
      style={{ minHeight }}
      onInput={handleInput}
      onPaste={(e) => {
        e.preventDefault();
        const text = safeClipboardRead(e);
        document.execCommand('insertText', false, text);
      }}
    />
  );
};

export default MarkdownSectionEditor;
